import type { ColumnDef } from "@tanstack/react-table";

import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import type { HelperOptions } from "@/components/DataTable/types";
import TooltipAlert from "@/components/Tooltip/TooltipAlert";
import { TooltipWrapper } from "@/components/Tooltip/TooltipWrapper";

/**
 * CSVインポート用のカラム幅（px）
 * ヘッダーとセルの両方でこの値を使用し、統一された幅を維持する
 */
const COLUMN_WIDTH = 120;

type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? `${K}` | `${K}.${NestedKeys<T[K]>}`
        : never;
    }[keyof T]
  : never;

// i18nKeyまたはdisplayLabelのどちらか一方が必須の型定義
type SimpleCellTextOptions =
  | (HelperOptions & { i18nKey: NonNullable<HelperOptions["i18nKey"]> })
  | (HelperOptions & {
      displayLabel: NonNullable<HelperOptions["displayLabel"]>;
    });

/** テキストを表示するカラム */
export function SimpleCellTextColumn<T>(
  id: NestedKeys<T> & string,
  options: SimpleCellTextOptions,
): ColumnDef<T, { value: string; errors?: string[] }> {
  const { i18nKey, displayLabel, minWidth, sortable = false } = options;
  return {
    accessorKey: id,
    meta: { i18nKey, displayLabel, thStyle: { minWidth } },
    enableSorting: sortable,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} maxWidth={COLUMN_WIDTH} />
    ),
    cell: ({ getValue }) => {
      const cellData = getValue();
      const value = cellData.value || "-";
      const hasErrors = (cellData.errors?.length ?? 0) > 0;

      const TEXT_LENGTH_THRESHOLD_FOR_TOOLTIP = 10;
      const isShortText =
        value === "-" || value.length <= TEXT_LENGTH_THRESHOLD_FOR_TOOLTIP;

      // エラーがある場合の表示
      if (hasErrors) {
        return (
          <TooltipAlert
            content={cellData.errors?.join("\n") ?? ""}
            value={value}
          />
        );
      }

      // 短いテキストの場合（Tooltipなし）
      if (isShortText) {
        return (
          <div
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ width: `${COLUMN_WIDTH}px` }}
          >
            {value}
          </div>
        );
      }

      // 長いテキストの場合（Tooltipあり）
      return (
        <TooltipWrapper delayDuration={500} content={value}>
          <div
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ width: `${COLUMN_WIDTH}px` }}
          >
            {value}
          </div>
        </TooltipWrapper>
      );
    },
  } satisfies ColumnDef<
    T,
    {
      value: string;
      errors?: string[];
    }
  >;
}
